import { ChevronLeft, ChevronRight, Package, Settings, Check } from 'lucide-react';
import { 
  PRIVACY_BOOTH_TECH_SPECS, 
  isPrivacyBooth, 
  getProductInclusions 
} from './ProductConstants';

// Tech Specs Slide Component
const TechSpecsSlide = ({ data }) => (
  <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-900 to-gray-900 flex flex-col items-center justify-start p-6 overflow-y-auto">
    <div className="text-center mb-4">
      <Settings className="w-10 h-10 text-primary mx-auto mb-2" />
      <h3 className="text-xl font-black uppercase text-white">Technical Specifications</h3>
      <p className="text-xs text-gray-400 mt-1 max-w-sm">{data.description}</p>
    </div>
    <div className="w-full max-w-md space-y-2">
      {data.specs.map((spec, index) => (
        <div key={index} className="flex bg-white/10 rounded-lg overflow-hidden">
          <div className="w-1/3 bg-primary/20 px-3 py-2 flex items-center">
            <span className="text-xs font-bold text-primary uppercase">{spec.label}</span>
          </div>
          <div className="w-2/3 px-3 py-2 flex items-center">
            <span className="text-xs text-gray-200 whitespace-pre-line">{spec.value}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Inclusions Slide Component
const InclusionsSlide = ({ data }) => {
  const inclusions = data?.inclusions || data || [];
  const exclusions = data?.exclusions || [];
  const hasExclusions = exclusions.length > 0;
  
  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-6 overflow-y-auto">
      <div className="text-center mb-4">
        <Package className="w-10 h-10 text-primary mx-auto mb-2" />
        <h3 className="text-xl font-black uppercase text-gray-800">What's Included</h3>
      </div>
      <div className={`grid ${inclusions.length <= 2 ? 'grid-cols-1' : 'grid-cols-2'} gap-2 max-w-md mb-4`}>
        {inclusions.map((item, index) => (
          <div key={index} className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg shadow-sm border border-green-200">
            <Check className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-xs font-medium text-gray-700">{item}</span>
          </div>
        ))}
      </div>
      
      {hasExclusions && (
        <>
          <div className="text-center mb-3 mt-2">
            <h3 className="text-lg font-black uppercase text-gray-600">Not Included</h3>
          </div>
          <div className={`grid ${exclusions.length <= 2 ? 'grid-cols-1' : 'grid-cols-2'} gap-2 max-w-md`}>
            {exclusions.map((item, index) => (
              <div key={index} className="flex items-center gap-2 bg-red-50 px-3 py-2 rounded-lg shadow-sm border border-red-200">
                <span className="w-4 h-4 text-red-500 flex-shrink-0 font-bold">✕</span>
                <span className="text-xs font-medium text-gray-600">{item}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export function ProductGallery({ product, currentImageIndex, setCurrentImageIndex }) {
  const productInclusionsData = getProductInclusions(product.subcategory);
  
  // Build images array from product data
  // Main image first, then gallery images (image_urls), then special slides
  const images = [
    { url: product.image_url, label: 'Product Photo', type: 'image' },
    // Add gallery images from image_urls array
    ...((product.image_urls || []).map((url, idx) => {
      // Determine label based on position or URL pattern
      let label = `Image ${idx + 2}`;
      if (url.includes('167gvdh5') || url.includes('1nmze7ur') || url.includes('s490q9mj') || url.includes('angr8w8c') || url.includes('4rpql5ql') || url.includes('5248yu0d')) {
        label = 'Top View';
      } else if (url.includes('a6sneu94') || url.includes('xx6et86o') || url.includes('2f2fvmsc') || url.includes('9dx1yy2a') || url.includes('yw6fh6y1') || url.includes('lmul1ptx')) {
        label = 'Front View';
      } else if (url.includes('jf6goi6y') || url.includes('4ngv3bak') || url.includes('xtzrest3')) {
        label = 'Side View';
      } else if (url.includes('tuqc0prt') || url.includes('ppsn7gvy')) {
        label = 'Dimensions';
      }
      return { url, label, type: 'image' };
    })),
    // Special slides for privacy booths
    ...(isPrivacyBooth(product.subcategory) ? [{ label: 'Tech Specs', type: 'techspecs' }] : []),
    ...(productInclusionsData ? [{ label: productInclusionsData.exclusions ? 'Inclusions & Exclusions' : 'Inclusions', type: 'component', data: productInclusionsData }] : [])
  ];
  
  const currentItem = images[currentImageIndex];

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div className="relative border border-border rounded-none overflow-hidden bg-white">
        {currentItem?.type === 'component' ? (
          <div className="w-full aspect-square">
            <InclusionsSlide data={currentItem.data} />
          </div>
        ) : currentItem?.type === 'techspecs' ? (
          <div className="w-full aspect-square">
            <TechSpecsSlide data={PRIVACY_BOOTH_TECH_SPECS} />
          </div>
        ) : (
          <img
            src={currentItem?.url}
            alt={`${product.name} - ${currentItem?.label}`}
            className="w-full aspect-square object-contain p-4"
            data-testid="product-detail-image"
            loading="eager"
          />
        )}
        
        {/* Image Label */}
        <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 text-sm font-bold uppercase">
          {currentItem?.label}
        </div>
        
        {/* Navigation Arrows */}
        <button
          onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6 text-gray-800" />
        </button>
        <button
          onClick={() => setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6 text-gray-800" />
        </button>
        
        {/* Dots Indicator */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                currentImageIndex === index ? 'bg-primary scale-110' : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`View image ${index + 1}`}
            />
          ))}
        </div>
      </div>
      
      {/* Thumbnail Strip */}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {images.map((item, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`flex-shrink-0 w-20 h-20 border-2 rounded-none overflow-hidden transition-all ${
              currentImageIndex === index 
                ? 'border-primary ring-2 ring-primary/30' 
                : 'border-border hover:border-gray-400'
            }`}
          >
            {item.type === 'component' ? (
              <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-primary" />
              </div>
            ) : item.type === 'techspecs' ? (
              <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                <Settings className="w-6 h-6 text-primary" />
              </div>
            ) : (
              <img
                src={item.url}
                alt={item.label}
                className="w-full h-full object-contain bg-white p-1"
                loading="lazy"
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
